import { Eyebrow, LinkButton, Pill } from '@/components/ui';
import { cn } from '@/lib/format';
import { ArrowRightIcon, CheckIcon, SparkIcon } from '@/lib/icons';

type Variant = 'outline' | 'secondary' | 'primary';

const TIERS: Array<{
  id: 'free' | 'coach' | 'gym' | 'gym_plus' | 'scale';
  name: string;
  price: string;
  unit: string | null;
  seats: string;
  note: string;
  cta: string;
  href: string;
  variant: Variant;
  wedge: boolean;
}> = [
  {
    id: 'coach',
    name: 'Coach',
    price: '£19.99',
    unit: '/ month',
    seats: 'Up to 10 clients · no internal coaches',
    note: 'For managing your own clients — no internal coaches.',
    cta: 'Choose Coach',
    href: 'mailto:info@lift2fit.com?subject=Lift2Fit%20CRM%20—%20Coach',
    variant: 'outline',
    wedge: true,
  },
  {
    id: 'gym',
    name: 'Gyms',
    price: '£49',
    unit: '/ month',
    seats: 'Up to 20 clients · 10 internal coaches',
    note: 'The specialised coach for gyms: your clients plus internal coaches manage them.',
    cta: 'Choose Gyms',
    href: 'mailto:info@lift2fit.com?subject=Lift2Fit%20CRM%20—%20Gyms',
    variant: 'outline',
    wedge: false,
  },
  {
    id: 'gym_plus',
    name: 'Gyms Plus',
    price: '£99',
    unit: '/ month',
    seats: 'Up to 100 clients · 20 internal coaches',
    note: 'For larger gyms: your clients plus a bigger internal coaching team manages them.',
    cta: 'Choose Gyms Plus',
    href: 'mailto:info@lift2fit.com?subject=Lift2Fit%20CRM%20—%20Gyms%20Plus',
    variant: 'outline',
    wedge: false,
  },
  {
    id: 'scale',
    name: 'Scale',
    price: 'Talk to us',
    unit: null,
    seats: 'Unlimited clients & internal coaches',
    note: 'Negotiated pricing — large customers talk to us.',
    cta: 'info@lift2fit.com',
    href: 'mailto:info@lift2fit.com?subject=Lift2Fit%20CRM%20—%20Scale',
    variant: 'secondary',
    wedge: false,
  },
];

const PAID_INCLUDES = [
  'Summary: clients, reviews due, revenue, the coach’s own week',
  'Calendar & bookings: availability slots with enforced capacity',
  'Clients & programmes: read, edit and prescribe',
  'Payments & revenue: who has paid, who is due',
  'AI assistance across the roster',
  'Chat: message a client from their summary',
];

/**
 * CRM pricing, exactly as platform plan §2 sets it and nothing invented. The
 * free tier for one client is the wedge; AI assistance is included in the paid
 * tiers and is never a separate SKU.
 */
export function CrmPricing() {
  return (
    <section id="crm-pricing" className="border-b border-hairline bg-page-subtle py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-10 flex flex-col items-start gap-4">
          <Eyebrow>CRM pricing</Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            Coaching plans built around your clients.
          </h2>
          <p className="max-w-[76ch] text-body-lg text-ink-muted">
            Coaching is a paid product — the free app is the lifter one. The tiers differ by how many
            clients you hold and how many internal coaches can help manage them: Coach is for managing
            your own clients, and Gyms is the specialised coach for gyms, where your clients plus internal
            coaches manage them. Prices are in GBP and billed monthly.
          </p>
          <p className="max-w-[76ch] text-[14px] text-ink-muted">
            Inviting a coach is part of the plan, not an extra: they join with the account that owns the
            email you invite, keep the whole workspace for the clients you assign, and never see your
            payments. Their <em>own</em> client book needs their own Coach plan — a gym plan does not pay
            for it.
          </p>
          <Pill tone="accent">
            <SparkIcon className="text-xs" /> AI assistance is included in the paid tiers
          </Pill>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              data-testid={`crm-tier-${tier.id}`}
              className={cn(
                'relative flex flex-col overflow-hidden rounded-card border bg-surface p-6',
                tier.wedge ? 'border-accent shadow-modal' : 'border-hairline shadow-card',
              )}
            >
              {tier.wedge ? <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-accent" /> : null}

              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-[20px] font-black uppercase tracking-[-0.01em] text-ink">
                  {tier.name}
                </h3>
                {tier.wedge ? <Pill tone="accent">The wedge</Pill> : null}
              </div>

              <p className="l2f-tabular mt-4 font-mono text-3xl font-bold leading-none text-ink">
                {tier.price}
                {tier.unit ? <span className="ml-2 text-[13px] font-semibold text-ink-muted">{tier.unit}</span> : null}
              </p>

              <p className="mt-4 border-y border-hairline py-3 text-[13px] font-bold text-ink">{tier.seats}</p>

              <p className="mt-3 flex-1 text-[13px] leading-relaxed text-ink-muted">{tier.note}</p>

              <LinkButton
                variant={tier.variant}
                size="md"
                href={tier.href}
                className={cn('mt-5', tier.id === 'scale' && 'normal-case tracking-[0.04em]')}
              >
                {tier.cta}
                {tier.id === 'scale' ? null : <ArrowRightIcon className="text-sm" />}
              </LinkButton>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="rounded-card border border-hairline bg-surface p-6 shadow-card">
            <h3 className="mb-3 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.08em] text-ink">
              <SparkIcon className="text-sm text-accent" /> AI assistance, inside the paid tiers
            </h3>
            <p className="text-[13px] leading-relaxed text-ink-muted">
              Risk flags, churn warnings and suggested interventions are the CRM’s premium layer — priced inside
              the Coach, Gyms, Gyms Plus and Scale tiers rather than sold as a separate SKU. It is the same
              evaluation matrix that powers the lifter’s AI coach, read across a roster.
            </p>
          </div>

          <div className="rounded-card border border-hairline bg-surface p-6 shadow-card">
            <h3 className="mb-3 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.08em] text-ink">
              <CheckIcon className="text-sm text-accent" /> Invite your own clients
            </h3>
            <p className="text-[13px] leading-relaxed text-ink-muted">
              Coaches and gyms can invite their own clients onto the platform. A pending invite reserves a seat,
              accepting keeps it, and revoking frees it. A large customer who needs more than a hundred clients —
              or more than twenty internal coaches — talks to us at{' '}
              <span className="font-bold text-ink">info@lift2fit.com</span>.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-card border border-hairline bg-surface p-6 shadow-card">
          <h3 className="mb-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink">
            Every paid tier includes
          </h3>
          <ul className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {PAID_INCLUDES.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-[13px] leading-snug text-ink">
                <CheckIcon className="mt-[2px] shrink-0 text-sm text-accent" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[12px] leading-relaxed text-ink-muted">
            Internal coaches come with the Gyms tiers: up to 10 on Gyms and up to 20 on Gyms Plus. Coach is for
            managing your own clients, and Scale is negotiated.
          </p>
          <p className="mt-3 border-t border-hairline pt-4 text-[11px] text-ink-muted">
            Lift2Fit is a training log, not a medical device. Nothing here is medical advice.
          </p>
        </div>
      </div>
    </section>
  );
}
